import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 border-2 border-red-500/50 bg-red-900/20 rounded-lg text-red-400 max-w-2xl mx-auto">
          <div className="flex items-start gap-3 mb-4">
            <div className="text-2xl">⚠️</div>
            <div className="flex-1">
              <h2 className="font-orbitron font-bold text-lg mb-2">Something went wrong</h2>
              <p className="text-sm text-red-300 mb-3">
                A component error occurred. Check the console for details.
              </p>
              <details className="text-sm">
                <summary className="cursor-pointer text-red-300 hover:text-red-200 mb-2">
                  ► Error details
                </summary>
                <pre className="mt-2 text-xs overflow-auto bg-black/50 p-3 rounded border border-red-500/30 text-red-200">
                  {this.state.error?.message || this.state.error?.toString() || 'Unknown error'}
                  {this.state.error?.stack && (
                    <div className="mt-2 text-xs opacity-75">
                      {this.state.error.stack.split('\n').slice(0, 5).join('\n')}
                    </div>
                  )}
                </pre>
              </details>
            </div>
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 border border-red-500/50 text-red-300 rounded hover:bg-red-500/20 transition-colors font-orbitron text-sm"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}



